/**
 * Ljus catalog sections on /produkter?category=Ljus.
 * Classification is slug/id — display names must not move sections.
 * A product may belong to multiple sections (dual-publish).
 */

export type LjusTierId = 'festljus' | 'stamningsljus' | 'scen_effektljus';

export const LJUS_TIER_STICKY_NAV = false;

export const LJUS_TIER_ORDER: readonly LjusTierId[] = [
  'festljus',
  'stamningsljus',
  'scen_effektljus',
] as const;

export const LJUS_TIER_COPY: Readonly<
  Record<LjusTierId, { title: string; blurb: string }>
> = {
  festljus: {
    title: 'Festljus',
    blurb: 'För dansgolv, DJ och fest – ljuseffekter som skapar energi och rörelse.',
  },
  stamningsljus: {
    title: 'Stämningsljus',
    blurb:
      'För att färgsätta lokalen och skapa rätt känsla för middagar, bröllop och event.',
  },
  scen_effektljus: {
    title: 'Scen & effektljus',
    blurb: 'Professionellt ljus för scen, DJ, live och större eventproduktioner.',
  },
};

/** Explicit live mapping (verified 2026-09-17). Values are one or more sections. */
export const LJUS_TIER_BY_SLUG: Readonly<Record<string, readonly LjusTierId[]>> = {
  // Festljus
  'beamz-party-bar-2-med-stativ-och-ljuseffekter': ['festljus'],
  'max-partybar-10-med-stativ-led-spots-och-ljuseffekter': ['festljus'],
  'gigbar-move-ils-allt-i-ett-ljuseffekt-for-fest-scen-brollop': ['festljus'],
  'lilla-ljuspaketet-smidig-effektfull-festbelysning': ['festljus'],
  'mellan-ljuspaketet-effekt-fargsattning': ['festljus'],
  'stora-ljuspaketet-effektljus-rokmaskin': ['festljus'],
  // Hidden today — mapped for when re-shown
  'chauvet-fxpar-6': ['festljus'],
  'chauvet-mini-kinta': ['festljus'],
  // Stämningsljus
  'omgivningsbelysning-4-pack-uplights': ['stamningsljus'],
  'omgivningsbelysning-6-pack-uplights': ['stamningsljus'],
  'omgivningsbelysning-10-pack-uplights': ['stamningsljus'],
  // Dual-publish: stämning + scen
  '4-st-kraftfulla-par-ljus': ['stamningsljus', 'scen_effektljus'],
  // Scen & effektljus
  'led-strobe-pixel-bar': ['scen_effektljus'],
  'kraftfull-vattentalig-350w-pixel-strobe': ['scen_effektljus'],
  'tva-kraftfulla-vattentalig-350w-pixel-strobar': ['scen_effektljus'],
  'cameo-thunder-wash': ['scen_effektljus'],
  'flood-wash': ['scen_effektljus'],
  'kraftfulla-moving-head-wash-lampor-2-pack': ['scen_effektljus'],
  '4-x-moving-head-wash-for-fest-scen-event': ['scen_effektljus'],
  'moving-head-beam': ['scen_effektljus'],
  'moving-head-spot': ['scen_effektljus'],
  'moving-head-wash': ['scen_effektljus'],
  'hybrid-moving-head-beam-spot-wash': ['scen_effektljus'],
};

export const LJUS_TIER_BY_ID: Readonly<Record<string, readonly LjusTierId[]>> = {
  '10805c86-f93d-4430-87de-9de78de2e804': ['festljus'],
  '6abdd5cd-44a5-40d7-8ca5-525344f94de2': ['festljus'],
  '8fd2d951-4f3c-457f-ae61-081d23add74c': ['festljus'],
  'a8ed5f79-2f15-4bc5-a3fb-1f09afff0d2c': ['festljus'],
  'e7f76a3a-efa0-4a4e-9748-aae15734a251': ['festljus'],
  '21c6ceef-6e14-42ed-bc04-fda2f5478735': ['festljus'],
  '398901ee-7c97-499b-9f04-be7217d315da': ['stamningsljus'],
  '559a892d-2170-405d-ace1-4315da958942': ['stamningsljus'],
  '6b554494-aa68-4365-a8ee-e44c06b05a95': ['stamningsljus'],
  'ce9fd0da-d5d1-47c0-9413-1c0866bf622f': ['stamningsljus', 'scen_effektljus'],
  '9c3503e1-74a0-43d2-ace9-2d0938cca6a2': ['scen_effektljus'],
  '3567f21e-b2bf-49ff-93d1-4336b4eb5e6b': ['scen_effektljus'],
  '77c6388e-1863-4c9d-9e4b-4bc0f7bcddcc': ['scen_effektljus'],
  'bdbd06a3-e0d6-4d7e-9bf8-d625422c36f7': ['scen_effektljus'],
  '46c6aae2-5ebf-4a7c-9b38-3fba9baed9f6': ['scen_effektljus'],
  'a71d2408-1ef9-4511-a798-784b0a4d8255': ['scen_effektljus'],
  '887c2b1b-aff6-4b2b-8a54-ee00bd933e86': ['scen_effektljus'],
  'f2997c47-f23f-476f-9fa6-05e45087e156': ['scen_effektljus'],
  '8b87abba-fe0c-4cef-baf9-57c20427bc97': ['scen_effektljus'],
  '5ffc3f37-7854-4148-ad77-499a4766c9ea': ['scen_effektljus'],
  'a212a178-8f49-4159-9355-053287f3748f': ['scen_effektljus'],
};

export type LjusTierProductRef = {
  id?: string;
  slug?: string | null;
  name?: string;
};

/**
 * Resolve sections. Explicit slug, then id. Name is ignored for mapped products.
 * Unknown products fall back to festljus (defensive only).
 */
export function resolveLjusTiers(product: LjusTierProductRef): readonly LjusTierId[] {
  const slug = String(product.slug ?? '')
    .trim()
    .toLowerCase();
  if (slug && LJUS_TIER_BY_SLUG[slug]) {
    return LJUS_TIER_BY_SLUG[slug];
  }
  const id = String(product.id ?? '').trim();
  if (id && LJUS_TIER_BY_ID[id]) {
    return LJUS_TIER_BY_ID[id];
  }
  return ['festljus'];
}

/**
 * Within Festljus: light packages first in size order, then remaining products
 * keep their incoming relative order.
 */
export const LJUS_FESTLJUS_PINNED_SLUGS: readonly string[] = [
  'lilla-ljuspaketet-smidig-effektfull-festbelysning',
  'mellan-ljuspaketet-effekt-fargsattning',
  'stora-ljuspaketet-effektljus-rokmaskin',
] as const;

function festljusSortKey(product: LjusTierProductRef): number {
  const slug = String(product.slug ?? '')
    .trim()
    .toLowerCase();
  const pinned = LJUS_FESTLJUS_PINNED_SLUGS.indexOf(slug);
  return pinned === -1 ? LJUS_FESTLJUS_PINNED_SLUGS.length : pinned;
}

export function sortLjusTierProducts<T extends LjusTierProductRef>(
  tier: LjusTierId,
  products: readonly T[]
): T[] {
  if (tier !== 'festljus') return [...products];
  return [...products].sort((a, b) => {
    const ka = festljusSortKey(a);
    const kb = festljusSortKey(b);
    if (ka !== kb) return ka - kb;
    return 0; // stable for rest
  });
}

export type LjusTierGroup<T extends LjusTierProductRef> = {
  tier: LjusTierId;
  title: string;
  blurb: string;
  products: T[];
};

export function groupLjusProducts<T extends LjusTierProductRef>(
  products: readonly T[]
): LjusTierGroup<T>[] {
  const buckets: Record<LjusTierId, T[]> = {
    festljus: [],
    stamningsljus: [],
    scen_effektljus: [],
  };
  for (const product of products) {
    for (const tier of resolveLjusTiers(product)) {
      buckets[tier].push(product);
    }
  }
  return LJUS_TIER_ORDER.map((tier) => ({
    tier,
    title: LJUS_TIER_COPY[tier].title,
    blurb: LJUS_TIER_COPY[tier].blurb,
    products: sortLjusTierProducts(tier, buckets[tier]),
  })).filter((g) => g.products.length > 0);
}
